import { useMemo } from "react"
import { GetApp } from "../DemoApp"
import { DemoLocalization } from "../Components/LocalizedMessage"
import { Scene, SceneFrame } from "../Components/Scene"
import { TestShip, day } from "../../shared/Data/TestShip"
import { QuickStartShip, DEMO_TIME, DEMO_TIMEZONE, QuickStartCurrentLog, QuickStartSavedLogs, QuickStartShipUsers } from "../../shared/Data/QuickStartData"
import { LogbookContentView, LogbookContentShipView } from "../../shared/Views/LogbookContentView"
import { AccountSettingsView } from "../../app/Views/AccountSettingsView"
import { IAppState, ILogbookAppNMEAConnectionStatus, INavigationRecordingStatus, IDay, INMEAWindReference, INote, INoteAttachment, IShip, IStatus } from "../../app/Types"
import { CruiseCalendarView } from "../../app/Views/CruiseCalendarView"
import { CruiseView } from "../../app/Views/CruiseView"
import { DayView, BlankDayView } from "../../app/Views/DayView"
import { NMEAConfigurationView } from "../../app/Views/NMEAConfigurationView"
import { OverlayView } from "../../app/Views/OverlayView"
import { TripView } from "../../app/Views/TripView"
import { ShipEditView } from "../../app/Views/ShipEditView"
import { ShipInfoHeaderView } from "../../app/Views/ShipInfoHeaderView"
import { TimelineFade } from "../../app/Components/TimelineFade"
import { ShipUserSelect } from "../../app/Components/ShipUserSelect"
import { IShipUserCrewRole } from "../../app/Types/Generated/LogbookModel/ShipUser"

// Helper function to combine days with notes and attachments
function combineDaysWithNotes(days: IDay[], notes: INote[], attachments: INoteAttachment[]): IDay[] {
  return days.map((day) => {
    const dayNotes = notes.filter((n) => n.dayId == day.id);
    const notesWithAttachments = dayNotes.map((note) => ({
      ...note,
      attachments: attachments.filter((a) => a.noteId == note.id),
    }));
    return {
      ...day,
      notes: notesWithAttachments,
    };
  });
}

interface DemoStatus {
  id: number;
  cruiseId: number;
  tripId: number;
  dayId: number;
  accountId: number;
  shipId: number;
  createdAt: string;
  updatedAt: string;
  endLogId?: number | null;
  startLogId?: number | null;
  statusType: number;
  name?: string;
  value: number | null;
  data?: string;
}

export interface SceneViewProps {
  frames: SceneFrame[];
  onClose?: () => void;
  scene?: "concept" | "quickstart" | "quickstart-voyage";
  initialFrame?: number;
}

export function SceneView({ frames, onClose, scene = "concept", initialFrame }: SceneViewProps) {
  const app = GetApp();
  const data = useMemo(() => {
    return TestShip();
  }, []);
  const dataBlank = useMemo(() => {
    return QuickStartShip();
  }, []);

  // all demo should shown cruise 9
  const renderCruise = (options?: { cruiseDetails?: boolean; cruiseTab?: string; tripDetails?: Record<number, boolean>; tripTabs?: Record<number, string>; dayDetails?: Record<number, boolean>; dayTabs?: Record<number, string>; dayHighlight?: Record<number, number>; daySelected?: Record<number, boolean>; logHighlight?: number; logTabs?: Record<number, string> }) => {
    const cruiseId = 9;
    const selectedCruise = (data.cruises ?? []).find((c) => c.id === cruiseId)!;
    const trips = data.trips?.filter((t) => t.cruiseId === cruiseId) || [];

    return (
      <LogbookContentShipView language={app.language} data={data}>
        <ShipInfoHeaderView actions={[]} customMenus={[]} shipHeader={() => <></>} />
        <CruiseView cruiseId={cruiseId} initialCruise={selectedCruise} initDetails={options?.cruiseDetails ?? false} initTab={options?.cruiseTab} initTracks={data.tracks?.filter((t) => t.cruiseId == cruiseId)} initTrips={trips}>
          <div className="CruiseView-content">
            {trips.length > 0 && <TimelineFade before={true} count={8} width={18} />}
            {trips.map((trip) => {
              const tripDays = data.days?.filter((d) => d.tripId == trip.id) || [];
              const tripNotes = data.notes?.filter((n) => n.tripId == trip.id) || [];
              const noteIds = tripNotes.map((n) => n.id);
              const tripAttachments = data.attachments?.filter((a) => noteIds.includes(a.noteId || 0)) || [];
              const days = combineDaysWithNotes(tripDays, tripNotes, tripAttachments);
              const showDetails = options?.tripDetails?.[trip.id];
              const selectedTab = options?.tripTabs?.[trip.id];
              return (
                <TripView
                  key={trip.id}
                  currentDays={days}
                  trip={trip}
                  overrideDays={days}
                  initDetails={showDetails}
                  initTracks={data.tracks?.filter((t) => t.tripId == trip.id)}
                  initTab={selectedTab}
                >
                  {days.map((day) => {
                    const hasDetails = day.startLocation || day.endLocation || day.calculated;
                    const logs = data.logs?.filter((l) => l.dayId == day.id) || [];
                    const showDayDetails = options?.dayDetails?.[day.id];
                    const selectedDayTab = options?.dayTabs?.[day.id];
                    const highlight = options?.dayHighlight?.[day.id] ?? (day.id === 6 && options?.logHighlight ? options.logHighlight : undefined);
                    const selected = options?.daySelected?.[day.id];
                    const logTabsWithId = options?.logHighlight && options?.logTabs?.[options.logHighlight]
                      ? { [options.logHighlight]: options.logTabs[options.logHighlight] }
                      : options?.logTabs;
                    return hasDetails ? (
                      <DayView
                        day={day}
                        key={day.id}
                        initDetails={showDayDetails}
                        initTab={selectedDayTab}
                        initHighlight={highlight}
                        initSelected={selected}
                        initLogs={logs.length > 0 ? logs : undefined}
                        initLogTabs={logTabsWithId}
                        initStatuses={data.statuses?.filter((s) => s.dayId == day.id)}
                        initTracks={data.tracks?.filter((t) => t.dayId == day.id)}
                      />
                    ) : <BlankDayView day={day} key={day.id} />;
                  })}
                </TripView>
              );
            })}
            {trips.length > 0 && <TimelineFade before={false} width={18} />}
          </div>
        </CruiseView>
      </LogbookContentShipView>
    );
  };

  // Helper function to render quickstart log entry scenes with common structure
  const renderQuickstartLogScene = (options: {
    time: Date;
    tripStartTime?: Date;
    logIds: number[];
    currentLogOptions: {
      sog?: number;
      course?: number;
      distance?: number;
      engineRpms?: number[];
      comment?: string;
      dayId?: number;
    };
    includeMotionStatus?: boolean;
    statusStartLogId?: number;
    showTomorrowDetails?: boolean;
    dayData?: Partial<{
      id: number;
      startLocation: string;
      skipper: number;
      crew: number[];
      status?: number;
      overnightStatus?: number;
    }>;
    dayTomorrowData?: Partial<{
      startLocation: string;
      skipper: number;
      crew: number[];
    }>;
    logHighlight?: number;
    logTabs?: Record<number, string>;
    logOverrides?: Record<number, Partial<{
      time: Date;
      sog: number;
      stw: number;
      course: number;
      heading: number;
      distance: number;
      depth: number;
      log: number;
      windDirection: number;
      windSpeed: number;
      pressure: number;
      weather: number;
      temperature: number;
      seaState: string;
      comment: string;
      engineRpms: number[];
      data: unknown;
      labels: string[];
      dayId: number;
      lat: number;
      lng: number;
    }>>;
    additionalStatuses?: DemoStatus[];
    shipOverride?: Partial<IShip>;
    initUserAdvice?: string;
    initUserAdviceData?: unknown;
    appStateUpdater?: (s: IAppState, time: Date) => IAppState;
  }) => {
    const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
    const currentTime = options.time;
    const currentDay = day(currentTime);
    const trip = {
      ...(dataBlank.trips ?? []).find((t) => t.id === 1)!,
      title: "My first passage",
      startedAt: (options.tripStartTime || currentTime).toISOString(),
    };

    // Day with start location and crew
    const dayData = {
      ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
      day: currentDay,
      startLocation: "Palma de Mallorca",
      skipper: 1,
      crew: [2, 3, 4],
      ...(options.dayData || {}),
    };

    // Tomorrow day (collapsed, all values 0)
    const dayTomorrow = {
      ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
      id: 0,
      day: currentDay + 1,
      ...(options.dayTomorrowData || {}),
    };

    // Customize the current log data
    const currentLog = QuickStartCurrentLog({
      time: currentTime,
      sog: options.currentLogOptions.sog ?? 0,
      course: options.currentLogOptions.course ?? 186,
      distance: options.currentLogOptions.distance ?? 0,
      engineRpms: options.currentLogOptions.engineRpms ?? [0, 0, 0],
      ...(options.currentLogOptions.dayId !== undefined && { dayId: options.currentLogOptions.dayId }),
      ...(options.currentLogOptions.comment !== undefined && { comment: options.currentLogOptions.comment }),
    });

    // Include skipper and crew
    const shipUsers = QuickStartShipUsers().filter((su) => [1, 2, 3, 4].includes(su.userId));

    // Create motion status if requested
    const statuses: DemoStatus[] = [
      ...(options.includeMotionStatus ? [{
        id: 1,
        cruiseId: 1,
        tripId: 1,
        dayId: 1,
        accountId: 1,
        shipId: 1,
        createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
        updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
        startLogId: options.statusStartLogId ?? 3,
        statusType: 1,
        value: 0,
      }] : []),
      ...(options.additionalStatuses || []),
    ];

    // Keep previous logs as saved entries
    const dataWithLog = {
      ...dataBlank,
      trips: [trip],
      cruises: [cruise],
      days: [dayData, dayTomorrow],
      users: dataBlank.users?.filter((user) => [1, 2, 3, 4].includes(user.id)),
      shipUsers: shipUsers,
      logs: QuickStartSavedLogs(options.logIds, options.logOverrides),
      statuses: statuses as unknown as IStatus[],
    };

    const appStateFunc = options.appStateUpdater
      ? (s: IAppState) => options.appStateUpdater!(s, currentTime)
      : (s: IAppState) => appStateNMEAStartedWithTime(s, currentTime);

    return (
      <LogbookContentShipView language={app.language}
        cloud={false}
        data={dataWithLog}
        focusActive={true}
        focusInitConfirmationType="trip"
        focusInitCruise={cruise}
        focusInitTrip={trip}
        focusInitDay={dayData}
        focusInitDayTomorrow={dayTomorrow}
        focusInitDayTomorrowDetails={options.showTomorrowDetails}
        focusInitLogs={dataWithLog.logs}
        focusInitCurrentLog={currentLog}
        focusInitStatuses={dataWithLog.statuses}
        focusInitLogHighlight={options.logHighlight}
        focusInitLogTabs={options.logTabs}
        focusInitUserAdvice={options.initUserAdvice}
        focusInitUserAdviceData={options.initUserAdviceData}
        updateAppState={appStateFunc}
      >
        <CruiseCalendarView initCruises={dataWithLog.cruises} />
      </LogbookContentShipView>
    );
  };

  return (
    <Scene content={{
    // view for start screen (Logos and title of the demo)
      "start": () => {
        const ui = (DemoLocalization[app.language as keyof typeof DemoLocalization] ?? DemoLocalization.en).ui;
        return (
          <LogbookContentView language={app.language} data={data}>
            <img src={ui.start_image} alt="LOGBOOK Concept" className="startimage" />
          </LogbookContentView>
        );
      },

      // view for end screen (buttons)
      "end": () => {
        return (
          <div className="final-screen">
            <img src="/demo/LOGBOOK.svg" alt="LOGBOOK" className="logo" />
          </div>
        );
      },

      "account": () => {
        return (
          <LogbookContentView language={app.language} data={data}>
            <AccountSettingsView cancelEdit={() => undefined} ships={data.ships} />
          </LogbookContentView>
        );
      },

      // view for overview timeline
      "timeline": () => {
        return (
          <LogbookContentShipView language={app.language} data={data}>
            <CruiseCalendarView initCruises={data.cruises} />
          </LogbookContentShipView>
        );
      },

      // view for cruise, showing different states of the cruise (details, tabs)
      "cruise": () => renderCruise(),
      "cruise-details": () => renderCruise({
        cruiseDetails: true,
        cruiseTab: "cruise",
      }),
      "cruise-details-stats": () => renderCruise({
        cruiseDetails: true,
        cruiseTab: "stats",
      }),
      "cruise-details-track": () => {
        return (
          <div className="demo-track-static" style={{ "--track-image": "url(\"/demo/track.png\")" } as React.CSSProperties}>
            {renderCruise({ cruiseDetails: true, cruiseTab: "track" })}
          </div>
        );
      },

      // view for trips, showing different states of the trip (details, tabs) trip #1
      "cruise-trip": () => renderCruise({
        tripDetails: { 1: true },
        tripTabs: { 1: "trip" },
      }),
      "cruise-stats": () => renderCruise({
        tripDetails: { 1: true },
        tripTabs: { 1: "stats" },
      }),
      "cruise-track": () => renderCruise({
        tripDetails: { 1: true },
        tripTabs: { 1: "track" },
      }),

      // view for days, showing different states of the day (details, tabs)  day #6
      "day": () => renderCruise({
        dayDetails: { 6: false },
        daySelected: { 6: true },
      }),
      "day-day": () => renderCruise({
        dayDetails: { 6: true },
        daySelected: { 6: true },
      }),
      "day-stats": () => renderCruise({
        dayDetails: { 6: true },
        dayTabs: { 6: "stats" },
        daySelected: { 6: true },
      }),
      "day-journal": () => renderCruise({
        dayDetails: { 6: true },
        dayTabs: { 6: "note-journal" },
        daySelected: { 6: true },
      }),

      // view for logs, showing different states of the log (details, tabs)
      "log-navigation": () => renderCruise({
        dayDetails: { 6: false },
        daySelected: { 6: true },
        logHighlight: 23,
        logTabs: { 23: "navigation" },
      }),
      "log-environment": () => renderCruise({
        dayDetails: { 6: false },
        daySelected: { 6: true },
        logHighlight: 23,
        logTabs: { 23: "environment" },
      }),

      // view for a screenshot from /demo/static/
      "screenshot": (frame) => {
        return (
          <div className="screenshot-view">
            <LogbookContentView language={app.language} data={data}>
              <img src={`/demo/${frame.contentOptions?.screenshot}`} alt="Screenshot" className="screenshot-image" />
            </LogbookContentView>
          </div>
        );
      },

      //  Views for Quickstart

      "start_solo": () => {
        const ui = (DemoLocalization[app.language as keyof typeof DemoLocalization] ?? DemoLocalization.en).ui;
        return (
          <LogbookContentShipView language={app.language} data={data}>
            <ShipInfoHeaderView actions={[]} customMenus={[]} />
            <div style={{ position: "relative" }}>
              <img src={ui.start_solo_image} alt="LOGBOOK" className="startimage" />
            </div>
          </LogbookContentShipView>
        );
      },
      "start_quickstart": () => {
        const ui = (DemoLocalization[app.language as keyof typeof DemoLocalization] ?? DemoLocalization.en).ui;
        return (
          <LogbookContentView language={app.language} data={data} hideMenus={true}>
            <div style={{ position: "relative" }}>
              <img src={ui.start_solo_image} alt="LOGBOOK" className="startimage" />
              <h1 className="chapter-title">{ui.quickstartChapter}</h1>
            </div>
          </LogbookContentView>
        );
      },

      "start_account": () => {
        return (
          <LogbookContentView language={app.language} cloud={true} data={dataBlank} hideShipMenu={true}>
            <AccountSettingsView cancelEdit={() => {}} ships={[]} />
          </LogbookContentView>
        );
      },
      "create_ship": () => {
        return (
          <LogbookContentView language={app.language} cloud={true} data={dataBlank} hideShipMenu={true}>
            <ShipEditView cancelEdit={() => {}} key={"new"} />
          </LogbookContentView>
        );
      },
      "create_ship_complete": () => {
        const ship = (dataBlank.ships ?? []).find((s) => s.id === 1)!;
        return (
          <LogbookContentView language={app.language} cloud={true} data={dataBlank} hideShipMenu={true}>
            <ShipEditView cancelEdit={() => {}} initShipName={ship.name} initShipType={ship.vesselType} initShipEngines={ship.engineCount} initValidInput={true} key={"new"} />
          </LogbookContentView>
        );
      },
      "new_ship_selected": () => {
        const dataWithoutShipInfo = {
          ...dataBlank,
          ship: {
            ...dataBlank.ship!,
            shipData: {
              ...dataBlank.ship!.shipData,
              mmsi: "",
              callsign: "",
              yachtModelName: "",
            },
          },
        } as typeof dataBlank;
        return (
          <LogbookContentShipView language={app.language} cloud={true} data={dataWithoutShipInfo}>
            <ShipInfoHeaderView actions={[]} customMenus={[]} shipHeader={() => <></>} />
            <CruiseCalendarView initCruises={[]} />
          </LogbookContentShipView>
        );
      },
      "new_ship_app": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEANotConfigured}>
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "new_ship_nmea": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEANotConfigured}>
            <OverlayView overlay={"nmea"} overlays={{ nmea: {
              close: true,
              render: () => <NMEAConfigurationView initConfiguration={{ type: "tcp", host: "192.168.4.1", port: 10110 }} />,
            } }}
            />
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "new_ship_nmea_empty": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEANotConfigured}>
            <OverlayView overlay={"nmea"} overlays={{ nmea: {
              close: true,
              render: () => <NMEAConfigurationView />,
            } }}
            />
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "new_ship_nmea_select": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEANotConfigured}>
            <OverlayView overlay={"nmea"} overlays={{ nmea: {
              close: true,
              render: () => <NMEAConfigurationView initConnectionTypeOpen={true} initConfiguration={{ type: "tcp" }} />,
            } }}
            />
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "new_ship_nmea_test": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEATestData}>
            <style>{`
          .status-nmea, .status-native { color: var(--green) !important; }
          .status-error { color: var(--red) !important; }
        `}</style>
            <OverlayView overlay={"nmea"} overlays={{ nmea: {
              close: true,
              render: () => <NMEAConfigurationView initTab="test_data" initConfiguration={{ type: "tcp", host: "192.168.4.1", port: 10110 }} />,
            } }}
            />
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },

      "new_ship_ready": () => {
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} updateAppState={appStateNMEATestData}>
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },

      // here for quickstart-voyage

      "start_quickstart_voyage": () => {
        const ui = (DemoLocalization[app.language as keyof typeof DemoLocalization] ?? DemoLocalization.en).ui;
        return (
          <LogbookContentView language={app.language} data={data} hideMenus={true}>
            <div style={{ position: "relative" }}>
              <img src={ui.start_solo_image} alt="LOGBOOK" className="startimage" />
              <h1 className="chapter-title">{ui.quickstartChapter2}</h1>
            </div>
          </LogbookContentView>
        );
      },

      "focus_start": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const trip = (dataBlank.trips ?? []).find((t) => t.id === 1)!;
        const currentDay = day(DEMO_TIME);
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
        };
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} focusActive={true} focusInitConfirmationType="trip" focusInitCruise={cruise} focusInitTrip={trip} focusInitDay={dayData} updateAppState={appStateNMEATestData}>
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "trip_name": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const trip = { ...(dataBlank.trips ?? []).find((t) => t.id === 1)!, title: "My first passage" };
        const currentDay = day(DEMO_TIME);
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
        };
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataBlank} focusActive={true} focusInitConfirmationType="trip" focusInitCruise={cruise} focusInitTrip={trip} focusInitDay={dayData} updateAppState={appStateNMEATestData}>
            <CruiseCalendarView initCruises={dataBlank.cruises} />
          </LogbookContentShipView>
        );
      },
      "trip_started": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const currentTime = DEMO_TIME;
        const currentDay = day(currentTime);
        const trip = {
          ...(dataBlank.trips ?? []).find((t) => t.id === 1)!,
          title: "My first passage",
          startedAt: currentTime.toISOString(),
        };

        // Get current log data from QuickStartData (for log row)
        const currentLog = QuickStartCurrentLog();

        // Today day (expanded, with current day data)
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
        };

        // Keep previous logs as saved entries
        const dataWithLog = {
          ...dataBlank,
          trips: [trip],
          cruises: [cruise],
          days: [dayData],
          logs: [],
        };

        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataWithLog} focusActive={true} focusInitConfirmationType="trip" focusInitCruise={cruise} focusInitTrip={trip} focusInitDay={dayData} focusInitCurrentLog={currentLog} focusInitStatuses={[]} updateAppState={appStateNMEAStarted}>
            <CruiseCalendarView initCruises={dataWithLog.cruises} />
          </LogbookContentShipView>
        );
      },

      "day_started": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const currentTime = DEMO_TIME;
        const currentDay = day(currentTime);
        const trip = {
          ...(dataBlank.trips ?? []).find((t) => t.id === 1)!,
          title: "My first passage",
          startedAt: currentTime.toISOString(),
        };

        // Day without start location and persons
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
        };

        // Customize the current log data
        const currentLog = QuickStartCurrentLog({
          sog: 0,
          course: 186,
          distance: 0,
        });

        // Select only skipper (user 1)
        const shipUsers = QuickStartShipUsers().filter((su) => su.userId === 1);

        const dataWithLog = {
          ...dataBlank,
          trips: [trip],
          cruises: [cruise],
          days: [dayData],
          users: dataBlank.users?.filter((user) => user.id === 1),
          shipUsers: shipUsers,
          logs: [],
        };
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataWithLog} focusActive={true} focusInitConfirmationType="trip" focusInitCruise={cruise} focusInitTrip={trip} focusInitDay={dayData} focusInitLogs={dataWithLog.logs} focusInitCurrentLog={currentLog} focusInitStatuses={[]} updateAppState={appStateNMEAStarted}>
            <CruiseCalendarView initCruises={dataWithLog.cruises} />
          </LogbookContentShipView>
        );
      },

      "day_data": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const currentTime = DEMO_TIME;
        const currentDay = day(currentTime);
        const trip = {
          ...(dataBlank.trips ?? []).find((t) => t.id === 1)!,
          title: "My first passage",
          startedAt: currentTime.toISOString(),
        };

        // Day with start location
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
          startLocation: "Palma de Mallorca",
          skipper: 1, // User ID 1 (Lucas Martin)
        };

        // Customize the current log data
        const currentLog = QuickStartCurrentLog({
          sog: 0,
          course: 186,
          distance: 0,
        });

        // Select only skipper (user 1)
        const shipUsers = QuickStartShipUsers().filter((su) => su.userId === 1);

        const dataWithLog = {
          ...dataBlank,
          trips: [trip],
          cruises: [cruise],
          days: [dayData],
          users: dataBlank.users?.filter((user) => user.id === 1),
          shipUsers: shipUsers,
          logs: [],
        };
        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataWithLog} focusActive={true} focusInitConfirmationType="trip" focusInitCruise={cruise} focusInitTrip={trip} focusInitDay={dayData} focusInitLogs={dataWithLog.logs} focusInitCurrentLog={currentLog} focusInitStatuses={[]} updateAppState={appStateNMEAStarted}>
            <CruiseCalendarView initCruises={dataWithLog.cruises} />
          </LogbookContentShipView>
        );
      },

      "day_data2": () => {
        const cruise = (dataBlank.cruises ?? []).find((c) => c.id === 1)!;
        const currentTime = DEMO_TIME;
        const currentDay = day(currentTime);
        const trip = {
          ...(dataBlank.trips ?? []).find((t) => t.id === 1)!,
          title: "My first passage",
          startedAt: currentTime.toISOString(),
        };

        // Day with start location
        const dayData = {
          ...(dataBlank.days ?? []).find((d) => d.id === 1)!,
          day: currentDay,
          startLocation: "Palma de Mallorca",
          skipper: 1, // User ID 1 (Lucas Martin)
        };

        // Customize the current log data
        const currentLog = QuickStartCurrentLog({
          sog: 0,
          course: 186,
          distance: 0,
        });

        // Select only skipper (user 1)
        const shipUsers = QuickStartShipUsers().filter((su) => su.userId === 1);

        const dataWithLog = {
          ...dataBlank,
          trips: [trip],
          cruises: [cruise],
          days: [dayData],
          users: dataBlank.users?.filter((user) => user.id === 1),
          shipUsers: shipUsers,
          logs: [],
        };

        return (
          <LogbookContentShipView language={app.language} cloud={false} data={dataWithLog} focusActive={true} focusInitConfirmationType="trip"
            focusInitCruise={cruise}
            focusInitTrip={trip}
            focusInitDay={dayData}
            focusInitLogs={dataWithLog.logs}
            focusInitCurrentLog={currentLog}
            focusInitStatuses={[]}
            updateAppState={appStateNMEAStarted}
            overlay={<ShipUserSelect dialogOnly={true} initAddShipUser={true} initUserName="Tom Schmidt" initUserShortname="Tom" name="crew" role={IShipUserCrewRole.crew} />}
          >
            <CruiseCalendarView initCruises={dataWithLog.cruises} />
          </LogbookContentShipView>
        );
      },

      "engine1_on": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)),
        logIds: [1],
        currentLogOptions: {
          sog: 0,
          course: 186,
          distance: 0,
          engineRpms: [0, 1200, 0],
        },
        // logHighlight: 1,
        showTomorrowDetails: false,
      }),

      "engine2_on": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)),
        logIds: [1, 2],
        currentLogOptions: {
          sog: 0,
          course: 186,
          distance: 0,
          engineRpms: [0, 1200, 1200],
        },
      }),

      "started": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)),
        logIds: [1, 2, 3],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        appStateUpdater: appStateNMEARecording,
      }),

      "entry_30min": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 0, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 0, 0)),
        logIds: [1, 2, 3, 4],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.4,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        appStateUpdater: appStateNMEARecording,
      }),

      "manualEntry_scene": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.8,
          engineRpms: [0, 1200, 1200],
          comment: "",
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        appStateUpdater: appStateNMEARecording,
      }),

      "sail_entry1": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4, 5],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.8,
          engineRpms: [0, 1200, 1200],
          comment: "",
        },
        includeMotionStatus: true,
        logHighlight: 5,
        logOverrides: { 5: { comment: "", stw: 5.7, heading: 186, log: 25812, depth: 35.6, lat: 39.551, lng: 2.632 } },
        statusStartLogId: 3,
        appStateUpdater: appStateNMEARecording,
      }),

      "sail_entry2": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4, 5],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.8,
          engineRpms: [0, 1200, 1200],
          comment: "",
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        logHighlight: 5,
        logOverrides: { 5: { comment: "", stw: 5.7, heading: 186, log: 25812, depth: 35.6, lat: 39.551, lng: 2.632, data: { nmeaFields: ["stw", "heading", "log", "depth", "course", "sog", "engineRpms"] } } },
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "entries_engines_off": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.9,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "entry_additional_data": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.9,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        logHighlight: 7,
        logTabs: { 7: "environment" },
        logOverrides: { 7: { data: { nmeaFields: ["windDirection", "windSpeed", "pressure", "temperature", "depth", "stw", "heading", "log", "course", "sog", "engineRpms"] } } },
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "entry_additional_data2": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7],
        currentLogOptions: {
          sog: 5.7,
          course: 186,
          distance: 0.9,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        logHighlight: 7,
        logTabs: { 7: "environment" },
        logOverrides: { 7: { weather: 2, seaState: "2", data: { nmeaFields: ["windDirection", "windSpeed", "pressure", "temperature", "depth", "stw", "heading", "log", "course", "sog", "engineRpms"] } } },
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "course_change": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 16, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 16, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8],
        currentLogOptions: {
          sog: 8.6,
          course: 126,
          distance: 2.3,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "manual_engine_entry": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 13, 16, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 13, 16, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8],
        currentLogOptions: {
          sog: 8.6,
          course: 126,
          distance: 2.3,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecordingManualEngine,
      }),

      "sailing": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
        currentLogOptions: {
          sog: 7.1,
          course: 133,
          distance: 23.4,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: true,
        statusStartLogId: 3,
        additionalStatuses: [
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 15,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "pause": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: false,
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 4,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            startLogId: 18,
            statusType: 1,
            value: 1,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "pause_dialog": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: false,
        logOverrides: {
          18: { labels: ["auto"] },
        },
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 4,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            startLogId: 18,
            statusType: 1,
            value: 1,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        initUserAdvice: "stop",
        appStateUpdater: appStateNMEARecording,
      }),

      "pause_set": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: false,
        logOverrides: {
          18: { labels: ["auto"] },
        },
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 4,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)).toISOString(),
            startLogId: 18,
            statusType: 1,
            value: 1,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        initUserAdvice: "stop",
        initUserAdviceData: {
          stopType: "endOfDay",
          endLocation: "Cala Palma",
          shipStatus: 3,
        },
        appStateUpdater: appStateNMEARecording,
      }),

      "pause_fixed": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: false,
        logOverrides: {
          18: { comment: "Stopped for end of day at Cala Palma" },
        },
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEARecording,
      }),

      "pause_fixed2": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 20, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 1200, 1200],
        },
        includeMotionStatus: false,
        logOverrides: {
          18: { comment: "Stopped for end of day at Cala Palma" },
        },
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEAStopped,
      }),

      "engines_off": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: false,
        logOverrides: {
          18: { comment: "Stopped for end of day at Cala Palma" },
        },
        additionalStatuses: [
          {
            id: 1,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)).toISOString(),
            endLogId: 18,
            startLogId: 3,
            statusType: 1,
            value: 0,
          },
          {
            id: 2,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 16,
            startLogId: 5,
            statusType: 4,
            name: "mainsail",
            value: 0,
          },
          {
            id: 3,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)).toISOString(),
            endLogId: 13,
            startLogId: 5,
            statusType: 4,
            name: "genoa",
            value: 0,
          },
        ],
        appStateUpdater: appStateNMEAStopped,
      }),

      "next_day": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        logIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
        currentLogOptions: {
          sog: 0,
          course: 133,
          distance: 23.7,
          engineRpms: [0, 0, 0],
        },
        includeMotionStatus: false,
        showTomorrowDetails: true,
        dayTomorrowData: {
          startLocation: "Cala Palma",
          skipper: 1,
          crew: [2, 3, 4],
        },
        logOverrides: {
          18: { comment: "Stopped for end of day at Cala Palma" },
        },
        additionalStatuses: [
          {
            id: 4,
            cruiseId: 1,
            tripId: 1,
            dayId: 1,
            accountId: 1,
            shipId: 1,
            createdAt: new Date(Date.UTC(2026, 2, 29, 16, 17, 0)).toISOString(),
            updatedAt: new Date(Date.UTC(2026, 2, 29, 16, 17, 0)).toISOString(),
            startLogId: 18,
            statusType: 1,
            value: 3,
          },
        ],
        appStateUpdater: appStateNMEAStopped,
      }),

      "next_day_ready": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 30, 7, 22, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        logIds: [],
        currentLogOptions: {
          sog: 0,
          course: 57,
          distance: 0,
          engineRpms: [0, 0, 0],
          dayId: 2,
        },
        includeMotionStatus: false,
        dayData: {
          id: 2,
          startLocation: "Cala Palma",
          skipper: 1,
          crew: [2, 3, 4],
        },
        additionalStatuses: [],
        appStateUpdater: appStateNMEAStopped,
      }),

      "next_day_engines": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 30, 7, 25, 30)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        logIds: [21, 22],
        currentLogOptions: {
          sog: 0,
          course: 25,
          distance: 0,
          engineRpms: [0, 0, 0],
          dayId: 2,
        },
        includeMotionStatus: false,
        dayData: {
          id: 2,
          startLocation: "Cala Palma",
          skipper: 1,
          crew: [2, 3, 4],
        },
        logOverrides: {
          21: { dayId: 2 },
          22: { dayId: 2 },
        },
        additionalStatuses: [],
        appStateUpdater: appStateNMEARecording,
      }),

      "next_day_started": () => renderQuickstartLogScene({
        time: new Date(Date.UTC(2026, 2, 30, 7, 25, 0)),
        tripStartTime: new Date(Date.UTC(2026, 2, 29, 16, 30, 0)),
        logIds: [21, 22, 23],
        currentLogOptions: {
          sog: 0,
          course: 25,
          distance: 0,
          engineRpms: [0, 0, 0],
          dayId: 2,
        },
        includeMotionStatus: false,
        dayData: {
          id: 2,
          startLocation: "Cala Palma",
          skipper: 1,
          crew: [2, 3, 4],
        },
        logOverrides: {
          21: { dayId: 2 },
          22: { dayId: 2 },
          23: { dayId: 2, time: new Date(Date.UTC(2026, 2, 30, 7, 25, 0)) },
        },
        additionalStatuses: [],
        appStateUpdater: appStateNMEARecording,
      }),

    }} frames={frames} scene={scene} initialFrame={initialFrame} onClose={onClose}
    />
  );
}

function appStateNMEANotConfigured(s: IAppState): IAppState {
  if (s.status?.nmea) {
    s.status.nmea.connected = false;
    s.status.nmea.connectionStatus = ILogbookAppNMEAConnectionStatus.notConfigured;
  }
  return s;
}

// this are the data for the NMEA test

function appStateNMEATestData(s: IAppState): IAppState {
  if (s.status) {
    s.status.time = DEMO_TIME.toISOString();
    s.status.systemTimezone = DEMO_TIMEZONE;
    s.status.navigation = false;
  }
  if (s.status?.nmea) {
    s.status.nmea.connected = true;
    s.status.nmea.connectionStatus = ILogbookAppNMEAConnectionStatus.connected;
    s.status.nmea.recordingStatus = INavigationRecordingStatus.paused;
    s.status.nmea.data = {
      ...s.status.nmea.data,
      navigation: {
        nmeaType: "RMC",
        source: 1, // INMEASource.nmea
        receivedAt: DEMO_TIME.toISOString(),
        time: DEMO_TIME.toISOString(),
        lat: 43.5,
        lng: 7.1,
        sog: 0,
        track: 180,
      },
      heading: [{
        nmeaType: "VTG",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        type: "VTG",
        courseTrue: 180,
        courseMagnetic: 185,
        speed: 0,
      }],
      depth: [{
        nmeaType: "DBT",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        type: "DBT",
        meters: 12.5,
        feet: 41,
        fathoms: 6.8,
      }],
      wind: [{
        nmeaType: "wind-relative",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        reference: INMEAWindReference.apparent,
        priority: false,
        relative: true,
        angle: 45,
        speed: 8.5,
      }],
      engine: [
        {
          nmeaType: "RPM",
          source: 1,
          receivedAt: DEMO_TIME.toISOString(),
          number: 1,
          rpm: 0,
        },
        {
          nmeaType: "RPM",
          source: 1,
          receivedAt: DEMO_TIME.toISOString(),
          number: 2,
          rpm: 0,
        },
      ],
    };
  }
  return s;
}

// NMEA started with engine data for the demo
// Engines show as running with RPM from NMEA, not manual buttons
function appStateNMEAStarted(s: IAppState): IAppState {
  return appStateNMEAStartedWithTime(s, DEMO_TIME);
}

// NMEA started with custom time (for engine scenes that use different time)
export function appStateNMEAStartedWithTime(s: IAppState, time: Date): IAppState {
  if (s.status) {
    s.status.time = time.toISOString();
    s.status.systemTimezone = DEMO_TIMEZONE;
    s.status.navigation = true;
  }
  if (s.status?.nmea) {
    s.status.nmea.connected = true;
    s.status.nmea.connectionStatus = ILogbookAppNMEAConnectionStatus.connected;
    s.status.nmea.recordingStatus = INavigationRecordingStatus.paused;
    s.status.nmea.data = {
      navigation: {
        nmeaType: "RMC",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        time: DEMO_TIME.toISOString(),
        lat: 0,
        lng: 0,
        sog: 0,
        track: 0,
      },
      heading: [{
        nmeaType: "VTG",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        type: "VTG",
        courseTrue: 180,
        courseMagnetic: 185,
        speed: 0,
      }],
      depth: [{
        nmeaType: "DBT",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        type: "DBT",
        meters: 12.5,
        feet: 41,
        fathoms: 6.8,
      }],
      wind: [{
        nmeaType: "wind-relative",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        reference: INMEAWindReference.apparent,
        priority: false,
        angle: 310,
        relative: true,
        speed: 15.5,
      }],
      weather: {
        nmeaType: "MDA",
        source: 1,
        receivedAt: DEMO_TIME.toISOString(),
        priority: false,
        pressure: 1013.5,
        temperature: 25,
        humidity: 0,
      },
      engine: [
        {
          nmeaType: "RPM",
          source: 1,
          receivedAt: DEMO_TIME.toISOString(),
          number: 1,
          rpm: 850, // Engine 1 running
        },
        {
          nmeaType: "RPM",
          source: 1,
          receivedAt: DEMO_TIME.toISOString(),
          number: 2,
          rpm: 850, // Engine 2 running
        },
      ],
    };
  }
  return s;
}

// NMEA recording active (for scenes where boat is in motion)
export function appStateNMEARecording(s: IAppState, time: Date): IAppState {
  s = appStateNMEAStartedWithTime(s, time);
  if (s.status?.nmea) {
    s.status.nmea.recordingStatus = INavigationRecordingStatus.recording;
  }
  // Keep demo logs inside the editable window without faking the clock: the
  // real `Time.timeUtc()` still runs, but `forceLogEditable` bypasses the
  // 1-hour recency check so recording scenes render an editable current log.
  s.forceLogEditable = true;
  return s;
}

// NMEA recording active but without engine NMEA data (for manual engine entry scenes)
export function appStateNMEARecordingManualEngine(s: IAppState, time: Date): IAppState {
  s = appStateNMEARecording(s, time);
  // Remove engine NMEA data to allow manual engine entry
  if (s.status?.nmea?.data) {
    delete s.status.nmea.data.engine;
  }
  return s;
}

// NMEA connected but recording stopped (automatic data entries off)
export function appStateNMEAStopped(s: IAppState, time: Date): IAppState {
  s = appStateNMEAStartedWithTime(s, time);
  if (s.status?.nmea) {
    s.status.nmea.recordingStatus = INavigationRecordingStatus.stopped;
  }
  // Keep demo logs editable without faking the clock (see appStateNMEARecording).
  s.forceLogEditable = true;
  return s;
}
